// The Circles directory's URL search: the one definition the route validates,
// the filter chrome reads and writes, and the list request and "is the list
// narrowed?" are derived from.
import { z } from 'zod';
import { isCircleFormat, type CircleFormat } from '@leanin/shared/circle-format';
import { CIRCLE_SEARCH_MAX } from '@leanin/shared/circle-limits';
import { LanguageTagSchema } from '@leanin/shared/language-tag';
import { TopicSlugSchema } from '@leanin/shared/topic-slug';
import { CountryCodeSchema, PLACE_TEXT_MAX } from '@leanin/shared/views/place';

import type { CircleFilterArgs, ListCirclesArgs } from '@/lib/queries/groups';
import { lenient } from '@/components/filters/index';

// The router JSON-decodes search values (`?q=12` arrives as the number 12);
// a text filter reads it back as the text the member typed.
const urlText = z.preprocess(
  (v) => (typeof v === 'number' || typeof v === 'boolean' ? String(v) : v),
  z.string(),
);

// Blank is absent, so `?q=%20` is no search everywhere (a lone typed space
// reads back as an empty box). Non-blank text is not trimmed here: the search
// box shows it while the member types, and trimming "new " would eat the space
// before the next word. The request trims it (`groupKeys.circle.list`).
const filledText = urlText.refine((s) => s.trim().length > 0);

// Past the API's caps the whole list request would fail, so an oversize value
// reads as absent. `q` is measured as sent (trimmed); `placeText` as is.
const searchText = filledText.refine((s) => s.trim().length <= CIRCLE_SEARCH_MAX);
const placeText = filledText.refine((s) => s.length <= PLACE_TEXT_MAX);

const formats = z
  .union([z.string(), z.array(z.string())])
  .transform((raw, ctx): CircleFormat[] => {
    const list = (Array.isArray(raw) ? raw : [raw]).filter(isCircleFormat);
    if (list.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'no known format' });
      return z.NEVER;
    }
    return list;
  });

// A value the API would reject (a malformed topic, language or country, an
// unknown format, an oversize search or place) or a repeated text param reads
// as absent (`lenient`); the rest of the search still applies.

// Legacy `?scope=` (the old My/All tabs, spec 2026-07-24-unified-group-
// directories) is not parsed, so old links land on the unified page unchanged.
export const circlesDirectorySearchSchema = z.object({
  q: lenient(searchText),
  topic: lenient(TopicSlugSchema),
  format: lenient(formats),
  language: lenient(LanguageTagSchema),
  placeText: lenient(placeText),
  placeCountryCode: lenient(CountryCodeSchema),
});

export type CirclesDirectorySearch = z.output<typeof circlesDirectorySearchSchema>;

// Every key named, so the filter chrome's "Clear all" (which commits the
// defaults) drops each of them from the URL.
export const CIRCLES_DIRECTORY_SEARCH_DEFAULTS: CirclesDirectorySearch = {
  q: undefined,
  topic: undefined,
  format: undefined,
  language: undefined,
  placeText: undefined,
  placeCountryCode: undefined,
};

/** Whether a search or any filter narrows the directory: decides the no-match state and narrows My Circles. */
export function hasActiveCircleFilters(s: CirclesDirectorySearch): boolean {
  return (
    s.q !== undefined ||
    s.topic !== undefined ||
    s.format !== undefined ||
    s.language !== undefined ||
    s.placeText !== undefined ||
    s.placeCountryCode !== undefined
  );
}

/** The search and filters as request params: the directory list and the
 *  viewer's own Circles take the same set, so the server narrows both lists
 *  with one predicate. */
export function circleFilterArgs(s: CirclesDirectorySearch): CircleFilterArgs {
  return {
    ...(s.q !== undefined ? { q: s.q } : {}),
    ...(s.topic !== undefined ? { topic: s.topic } : {}),
    ...(s.format !== undefined ? { format: s.format.join(',') } : {}),
    ...(s.language !== undefined ? { language: s.language } : {}),
    ...(s.placeText !== undefined ? { placeText: s.placeText } : {}),
    ...(s.placeCountryCode !== undefined ? { placeCountryCode: s.placeCountryCode } : {}),
  };
}

/**
 * The directory's list request. Browsing uses the ranked sort; a search drops
 * it and the server orders by relevance. The directory renders "Recommended
 * for you", so it asks for it; the server leaves it out of a search or a
 * filtered list.
 */
export function circlesDirectoryListArgs(s: CirclesDirectorySearch, pageSize: number): ListCirclesArgs {
  return {
    pageSize,
    surface: 'circles_directory',
    include: 'recommended',
    ...(s.q === undefined ? { sort: 'recommended' as const } : {}),
    ...circleFilterArgs(s),
  };
}
